/**
 * The billing (subscription, checkout, invoices) contract — Company Owner & HR Admin only.
 *
 * TENANT-FACING ONLY. Verifying an offline (NEFT/RTGS/cash-deposit) payment proof is NOT here —
 * that is a platform-admin action against Arham Fintech's own bank statement, never a tenant
 * permission (see `platform.contract.ts`'s billing routes and `OfflinePaymentProof`'s doc comment
 * in schema.prisma for why: a tenant confirming its own payment would be self-attestation).
 *
 * MONEY IS DECIMAL STRINGS, NEVER NUMBERS (invariant 2) — `decimalStringSchema` throughout, exactly
 * as `expense.contract.ts` does. Every price a client shows (the seat calculator, the checkout
 * order summary) is computed by `@hrms/shared`'s `computeSubscriptionPrice` — the SAME function the
 * API calls to charge — so a preview and a real charge can never disagree.
 *
 * INDIA-ONLY (INR) THIS SPRINT, DELIBERATELY. `placeOfSupplyState` still has to be threaded through
 * checkout now, though: CGST+SGST vs IGST is decided by Place-of-Supply-state vs Arham Fintech's
 * home state, which applies to every India customer, not just a future international one.
 *
 * EVERY MUTATION REQUIRES AN IDEMPOTENCY KEY (invariant 10) — checkout, seat changes, and proof
 * submission all move money or change what an org is entitled to; a retried request must return
 * the original result, never charge or provision twice.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  billingFrequencySchema,
  billingPaymentModeSchema,
  currencySchema,
  decimalStringSchema,
  errorBodySchema,
  idempotencyKeySchema,
  inStateSchema,
  instantSchema,
  invoiceStatusSchema,
  invoiceTypeSchema,
  moduleEntitlementSourceSchema,
  moduleKeySchema,
  offlinePaymentVerificationStatusSchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  subscriptionStatusSchema,
  uuidSchema,
} from '../schemas/common.js';
// Already validated (format + mod-36 checksum) for reimbursement's own merchant-GSTIN field — one
// definition, not a second copy here. See its own doc comment for why.
import { gstinSchema } from './expense.contract.js';
import { routeMeta } from './meta.js';

const c = initContract();

/** See expense.contract.ts's identical declaration for why this lives on the contract itself. */
const idempotencyHeadersSchema = z.object({
  'idempotency-key': idempotencyKeySchema,
});

// ---------------------------------------------------------------------------
// Module primitives
// ---------------------------------------------------------------------------

const modulePricingSchema = z.object({
  kind: z.enum(['per_seat', 'flat']),
  /** Monthly, decimal string. */
  rate: decimalStringSchema,
});
export type ModulePricingDto = z.infer<typeof modulePricingSchema>;

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

export const planSchema = z.object({
  id: uuidSchema,
  code: z.string(),
  name: z.string(),
  currency: currencySchema,
  /** Monthly, decimal string — covers `baseSeats` seats. */
  baseFee: decimalStringSchema,
  baseSeats: z.number().int().positive(),
  /** Monthly, decimal string — per seat above `baseSeats`. */
  extraSeatRate: decimalStringSchema,
  mandatoryModuleKeys: z.array(moduleKeySchema),
  /** Non-mandatory modules included by default; a subset may be toggled off at checkout. */
  includedModuleKeys: z.array(moduleKeySchema),
  /** Per-module rate for every non-mandatory included module — what toggling it off deducts.
   * Keyed by `ModuleKey` but declared `z.record(z.string(), ...)`, not an enum key schema — Zod
   * requires an enum-keyed record to be exhaustive, and this is deliberately a partial map (only
   * the plan's own togglable modules), same convention every other record in this codebase uses. */
  modulePricing: z.record(z.string(), modulePricingSchema),
});
export type PlanDto = z.infer<typeof planSchema>;

export const addonModuleSchema = z.object({
  id: uuidSchema,
  moduleKey: moduleKeySchema,
  name: z.string(),
  pricing: modulePricingSchema,
  currency: currencySchema,
});
export type AddonModuleDto = z.infer<typeof addonModuleSchema>;

export const subscriptionSchema = z.object({
  id: uuidSchema,
  organizationId: orgIdSchema,
  plan: planSchema,
  billingFrequency: billingFrequencySchema,
  status: subscriptionStatusSchema,
  seats: z.number().int().nonnegative(),
  deselectedModuleKeys: z.array(moduleKeySchema),
  /** Standalone add-ons active on top of the plan (source = 'addon' entitlements). */
  addonModuleKeys: z.array(moduleKeySchema),
  effectiveModuleKeys: z.array(moduleKeySchema),
  currentPeriodStart: instantSchema,
  currentPeriodEnd: instantSchema,
  /** Set when a cancellation is scheduled for period end rather than immediate — the subscription
   * stays `active` (still entitled) until `currentPeriodEnd`, then lapses without renewing. */
  cancelAtPeriodEnd: z.boolean(),
  /** A plan change queued to take effect at the next renewal, rather than prorated immediately. */
  scheduledPlanId: uuidSchema.nullable(),
  /** When this subscription was moved to `suspended` (past_due gone terminal) — null otherwise. */
  suspendedAt: instantSchema.nullable(),
  cancelledAt: instantSchema.nullable(),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type SubscriptionDto = z.infer<typeof subscriptionSchema>;

const gstBreakdownSchema = z.object({
  cgst: decimalStringSchema,
  sgst: decimalStringSchema,
  igst: decimalStringSchema,
  totalTax: decimalStringSchema,
  isInterState: z.boolean(),
  ratePercent: z.string(),
});

const priceLineSchema = z.object({
  label: z.string(),
  amount: decimalStringSchema,
});

export const priceBreakdownSchema = z.object({
  effectiveModuleKeys: z.array(moduleKeySchema),
  cycleMonths: z.number().int().positive(),
  durationDiscountPercent: z.number().int().nonnegative(),
  lines: z.array(priceLineSchema),
  subtotalBeforeDiscount: decimalStringSchema,
  durationDiscountAmount: decimalStringSchema,
  subtotal: decimalStringSchema,
  gst: gstBreakdownSchema,
  totalPayable: decimalStringSchema,
});
export type PriceBreakdownDto = z.infer<typeof priceBreakdownSchema>;

/** Vanguard HR's own beneficiary bank details, shown for the offline/bank-deposit path. Not
 * per-org — Vanguard HR is a single issuing entity, same reasoning as `Invoice.reference`. */
export const beneficiaryBankDetailsSchema = z.object({
  beneficiaryName: z.string(),
  bankName: z.string(),
  accountNumber: z.string(),
  ifscCode: z.string(),
  accountType: z.string(),
  paymentReferenceKey: z.string(),
});

export const offlinePaymentProofSchema = z.object({
  id: uuidSchema,
  invoiceId: uuidSchema,
  utrNumber: z.string(),
  receiptDocumentUrl: z.string(),
  submittedAt: instantSchema,
  verificationStatus: offlinePaymentVerificationStatusSchema,
  verifiedAt: instantSchema.nullable(),
  rejectionReason: z.string().nullable(),
  /** Set on verification (B-P7): what actually landed net of any TDS the payer deducted. Null until
   * then — the amount received is a fact the platform admin confirms against the bank statement,
   * never something the tenant declares up front. */
  amountReceived: decimalStringSchema.nullable(),
  /** Tax deducted at source by the payer before remitting — present only when the payer withheld
   * TDS on this payment; the invoice settles when amountReceived + tdsAmount equals the total. */
  tdsAmount: decimalStringSchema.nullable(),
  tdsSection: z.string().nullable(),
  tdsCertificateUrl: z.string().nullable(),
});
export type OfflinePaymentProofDto = z.infer<typeof offlinePaymentProofSchema>;

/** Rule 46 requires the buyer's and supplier's legal name, GSTIN and address to be printed on the
 * tax invoice AS THEY STOOD when it was issued — a later change to the org's billing profile or
 * Vanguard HR's own registration must never rewrite a past invoice. All nullable: absent on a
 * `pro_forma` (generated before any of this is confirmed) and on invoices issued before B-P2. */
const invoiceGstSnapshotSchema = z.object({
  buyerLegalName: z.string().nullable(),
  buyerGstin: z.string().nullable(),
  buyerAddress: z.string().nullable(),
  buyerStateCode: z.string().nullable(),
  supplierLegalName: z.string().nullable(),
  supplierGstin: z.string().nullable(),
  supplierAddress: z.string().nullable(),
  supplierStateCode: z.string().nullable(),
  /** Section 9(3)/9(4) reverse charge — always false for this product (subscription billing is
   * never a reverse-charge supply), carried on the invoice because Rule 46 requires the field to
   * be printed either way. */
  reverseCharge: z.boolean(),
});

export const invoiceSchema = z
  .object({
    id: uuidSchema,
    organizationId: orgIdSchema,
    subscriptionId: uuidSchema,
    invoiceType: invoiceTypeSchema,
    paymentMode: billingPaymentModeSchema.nullable(),
    reference: z.string().nullable(),
    subtotalAmount: decimalStringSchema,
    taxAmount: decimalStringSchema,
    totalAmount: decimalStringSchema,
    currency: currencySchema,
    status: invoiceStatusSchema,
    sacCode: z.string(),
    gst: gstBreakdownSchema.nullable(),
    paidAt: instantSchema.nullable(),
    bankDetails: beneficiaryBankDetailsSchema.nullable(),
    offlinePaymentProofs: z.array(offlinePaymentProofSchema),
    createdAt: instantSchema,
  })
  .extend(invoiceGstSnapshotSchema.shape);
export type InvoiceDto = z.infer<typeof invoiceSchema>;

/**
 * A short-lived private link to the stored invoice PDF, same shape as `payslipDownloadSchema` —
 * the link is issued only after `org.billing:read` has authorised the caller for this invoice, and
 * carries no password because the document has none.
 */
export const invoiceDownloadSchema = z.object({
  url: z.string().url(),
  expiresAt: instantSchema,
  fileName: z.string(),
});
export type InvoiceDownload = z.infer<typeof invoiceDownloadSchema>;

/** A `CreditNote` (Section 34) — always platform-admin issued against a paid tax invoice, never
 * tenant self-service (see schema.prisma's `CreditNote` doc comment for the maker-checker
 * reasoning). This is the DTO shape only; the issue/list routes are B-P8. */
export const creditNoteSchema = z.object({
  id: uuidSchema,
  organizationId: orgIdSchema,
  invoiceId: uuidSchema,
  reference: z.string().nullable(),
  amount: decimalStringSchema,
  taxAmount: decimalStringSchema,
  totalAmount: decimalStringSchema,
  currency: currencySchema,
  gst: gstBreakdownSchema.nullable(),
  reason: z.string(),
  createdAt: instantSchema,
});
export type CreditNoteDto = z.infer<typeof creditNoteSchema>;

export const orgModuleEntitlementSchema = z.object({
  moduleKey: moduleKeySchema,
  source: moduleEntitlementSourceSchema,
  validFrom: instantSchema,
});

/**
 * The org's billing/tax registration details (platform schema, `BillingProfile`) — legal name,
 * GSTIN, address, state and contacts. Null until the org has ever set one: `createCheckoutBodySchema`
 * still collects these fields inline for checkout (B-P1 scope note), so a fresh org can complete a
 * first purchase before ever visiting this page; from then on, `stateCode`/`gstin`/`legalName` here
 * are what B-P3's tax-invoice snapshot reads at issue time, not what checkout collected that once.
 */
export const billingProfileSchema = z.object({
  organizationId: orgIdSchema,
  legalName: z.string(),
  gstin: z.string().nullable(),
  addressLine1: z.string().nullable(),
  addressLine2: z.string().nullable(),
  city: z.string().nullable(),
  stateCode: inStateSchema.nullable(),
  postalCode: z.string().nullable(),
  contactName: z.string().nullable(),
  contactEmail: z.string().nullable(),
  contactPhone: z.string().nullable(),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type BillingProfileDto = z.infer<typeof billingProfileSchema>;

/**
 * One module's full state for the Settings → Modules screen (B-P5, KEKA_VS_BILLING.md §4.4):
 * `entitled` is the commercial truth (plan/add-on/manual grant); `isEnabled` is this org's own
 * switch (true when no row exists yet — the default, per `org_module_switches.is_enabled`'s own
 * column default). `enabled` is the combined result every other layer actually gates on — exactly
 * what `apps/api/src/lib/billing.ts`'s `writeModulesCache` computes into `Organization.settings.
 * modules`, exposed here so the toggle UI never has to recompute the AND itself and risk disagreeing
 * with what the API actually enforces.
 */
export const orgModuleStatusSchema = z.object({
  moduleKey: moduleKeySchema,
  entitled: z.boolean(),
  isEnabled: z.boolean(),
  enabled: z.boolean(),
  /** Never switchable — always entitled, always enabled, never appears with `enabled: false`. */
  isCoreHr: z.boolean(),
});
export type OrgModuleStatusDto = z.infer<typeof orgModuleStatusSchema>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

/** Shared by the preview and the real checkout — the real one adds `paymentMode` and org tax
 * details, everything price-relevant is identical so the two can never disagree on the formula. */
const checkoutPricingInputSchema = z.object({
  planCode: z.string(),
  billingFrequency: billingFrequencySchema,
  seatCount: z.number().int().positive().max(500),
  deselectedModuleKeys: z.array(moduleKeySchema).default([]),
  addonModuleKeys: z.array(moduleKeySchema).default([]),
  placeOfSupplyState: inStateSchema,
});

export const previewCheckoutBodySchema = checkoutPricingInputSchema;

export const createCheckoutBodySchema = checkoutPricingInputSchema.extend({
  paymentMode: billingPaymentModeSchema,
  /** Company tax & billing details — not yet a persisted "billing profile" entity (see this
   * module's phase plan); collected at checkout for this sprint. */
  legalName: z.string().min(1).max(200),
  gstin: z.string().trim().toUpperCase().optional(),
  /**
   * Which billing region this checkout is for. `india` (the default, so existing clients are
   * unchanged) is the Razorpay/offline INR + GST flow. `international` is Dodo Payments: USD, no
   * GST (export of services), online only, monthly or yearly only, fixed per-tier products (so
   * `deselectedModuleKeys`/`addonModuleKeys` do not apply and must be empty), seat count sent as
   * the product quantity. `placeOfSupplyState` is ignored for international.
   */
  region: z.enum(['india', 'international']).default('india'),
  /** ISO 3166-1 alpha-2 billing country — used only when `region: 'international'`. */
  billingCountry: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{2}$/)
    .optional(),
});

/** International (Dodo) price preview — the price lives on the Dodo product, so it is fetched
 * server-side; there is no client-side formula to run. Monthly/yearly only. */
export const previewInternationalCheckoutBodySchema = z.object({
  planCode: z.string(),
  billingFrequency: z.enum(['monthly', 'yearly']),
  seatCount: z.number().int().positive().max(500),
});

export const internationalPriceSchema = z.object({
  currency: currencySchema,
  unitPrice: decimalStringSchema,
  seatCount: z.number().int(),
  subtotal: decimalStringSchema,
  /** Always "0" — export of services, no GST. Dodo (Merchant of Record) may add buyer-side sales
   * tax/VAT at its own checkout; that is not part of this invoice. */
  taxAmount: decimalStringSchema,
  totalPayable: decimalStringSchema,
});

export const updateSeatsBodySchema = z.object({
  newSeatCount: z.number().int().positive().max(500),
});

/** `gstin` is optional — an org without a GST registration (e.g. below the threshold) still needs
 * a billing profile for the legal-name/address Rule 46 fields, just with reverse_charge / no-GSTIN
 * handling at invoice time (B-P4). */
export const upsertBillingProfileBodySchema = z.object({
  legalName: z.string().trim().min(1).max(200),
  gstin: gstinSchema.optional(),
  addressLine1: z.string().trim().min(1).max(200).optional(),
  addressLine2: z.string().trim().max(200).optional(),
  city: z.string().trim().min(1).max(100).optional(),
  stateCode: inStateSchema.optional(),
  postalCode: z.string().trim().max(20).optional(),
  contactName: z.string().trim().max(200).optional(),
  contactEmail: z.string().trim().email().max(320).optional(),
  contactPhone: z.string().trim().max(30).optional(),
});

/** `core_hr` is never switchable (§4.1) — rejected at the edge (422), same convention
 * `platform.contract.ts`'s `grantableModuleKeySchema` uses for manual grants. */
export const switchableModuleKeySchema = moduleKeySchema.exclude(['core_hr']);

export const setModuleSwitchBodySchema = z.object({
  isEnabled: z.boolean(),
});

export const submitOfflineProofBodySchema = z.object({
  utrNumber: z.string().trim().min(1).max(100),
  receiptDocumentUrl: z.string().url(),
});

export const listInvoicesQuerySchema = paginationQuerySchema.extend({
  status: invoiceStatusSchema.optional(),
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const billingContract = c.router(
  {
    listPlans: {
      method: 'GET',
      path: '/plans',
      responses: {
        200: z.object({ plans: z.array(planSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'List the plan catalogue (India/INR only this sprint)',
      metadata: routeMeta('org.billing:read'),
    },

    listAddonModules: {
      method: 'GET',
      path: '/addon-modules',
      responses: {
        200: z.object({ addonModules: z.array(addonModuleSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'List standalone add-on modules purchasable without a plan upgrade',
      metadata: routeMeta('org.billing:read'),
    },

    getSubscription: {
      method: 'GET',
      path: '/subscription',
      responses: {
        200: subscriptionSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        /** No subscription yet — the org has never checked out. */
        404: errorBodySchema,
      },
      summary: "Get the org's current subscription",
      metadata: routeMeta('org.billing:read'),
    },

    getBillingProfile: {
      method: 'GET',
      path: '/profile',
      responses: {
        200: billingProfileSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        /** The org has never set one — checkout still collects these fields inline until it has. */
        404: errorBodySchema,
      },
      summary: "Get the org's billing profile (legal name, GSTIN, address, contacts)",
      metadata: routeMeta('org.billing:read'),
    },

    upsertBillingProfile: {
      method: 'PUT',
      path: '/profile',
      body: upsertBillingProfileBodySchema,
      responses: {
        200: billingProfileSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        /** A malformed GSTIN — the format+checksum check, not a missing field. */
        422: errorBodySchema,
      },
      summary: 'Create or replace the billing profile — every future tax invoice snapshots this at issue time',
      metadata: routeMeta('org.billing:update'),
    },

    listModuleStatus: {
      method: 'GET',
      path: '/modules',
      responses: {
        200: z.object({ modules: z.array(orgModuleStatusSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Every module with its entitlement, switch and combined-enabled state — Settings → Modules',
      metadata: routeMeta('org.billing:read'),
    },

    setModuleSwitch: {
      method: 'POST',
      path: '/modules/:moduleKey/switch',
      pathParams: z.object({ moduleKey: switchableModuleKeySchema }),
      headers: idempotencyHeadersSchema,
      body: setModuleSwitchBodySchema,
      responses: {
        200: z.object({ modules: z.array(orgModuleStatusSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
        /** core_hr (rejected before this ever runs — pathParams schema excludes it, this is
         * belt-and-braces for the handler itself), or turning ON a module the org is not
         * entitled to — the Modules screen should have sent the admin to Checkout instead. */
        422: errorBodySchema,
      },
      summary: 'Turn one module on or off for the org — only ever a plain toggle for a module already entitled',
      metadata: routeMeta('org.billing:update'),
    },

    previewCheckout: {
      method: 'POST',
      path: '/checkout/preview',
      body: previewCheckoutBodySchema,
      responses: {
        200: priceBreakdownSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        /** Unknown plan code, or a deselected/addon module the plan does not offer. */
        422: errorBodySchema,
      },
      summary: 'Compute the price for a plan/seat/module selection without committing anything',
      metadata: routeMeta('org.billing:read'),
    },

    previewInternationalCheckout: {
      method: 'POST',
      path: '/checkout/preview-international',
      body: previewInternationalCheckoutBodySchema,
      responses: {
        200: internationalPriceSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
        /** Dodo is not configured, or this plan has no Dodo product. */
        412: errorBodySchema,
        503: errorBodySchema,
      },
      summary: 'Price an international (Dodo, USD) selection from the Dodo product, without committing',
      metadata: routeMeta('org.billing:read'),
    },

    createCheckout: {
      method: 'POST',
      path: '/checkout',
      headers: idempotencyHeadersSchema,
      body: createCheckoutBodySchema,
      responses: {
        200: z.object({
          subscription: subscriptionSchema,
          invoice: invoiceSchema,
          /** Present only when `paymentMode: 'online'` — the client opens Razorpay Checkout with it. */
          razorpayOrder: z.object({ orderId: z.string(), keyId: z.string(), amountPaise: z.number().int() }).nullable(),
          /** Present only for `region: 'international'` — redirect the browser to `checkoutUrl`
           * (Dodo's hosted payment page). The invoice is settled by Dodo's webhook, not by this. */
          dodoCheckout: z.object({ sessionId: z.string(), checkoutUrl: z.string().url() }).nullable(),
        }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
        /** `paymentMode: 'online'` was requested but no Razorpay gateway is configured — see
         * `apps/api/src/routes/billing.ts`'s `RazorpayGateway` seam. */
        412: errorBodySchema,
      },
      summary: 'Start or change a subscription — online (Razorpay order) or offline (pro-forma invoice)',
      metadata: routeMeta('org.billing:update'),
    },

    updateSeats: {
      method: 'POST',
      path: '/subscription/seats',
      headers: idempotencyHeadersSchema,
      body: updateSeatsBodySchema,
      responses: {
        200: z.object({
          subscription: subscriptionSchema,
          proratedInvoice: invoiceSchema,
        }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** No active subscription to change seats on. */
        409: errorBodySchema,
      },
      summary: "Change the org's seat count mid-cycle, prorated for the days remaining",
      metadata: routeMeta('org.billing:update'),
    },

    // NOT razorpayWebhook. A signed provider callback needs the RAW request bytes for HMAC
    // verification, which ts-rest's router cannot preserve — it parses JSON before a handler ever
    // sees `body`, and re-serialising a parsed object does not reproduce what was actually signed.
    // This codebase already has the correct shape for exactly this problem
    // (payroll-ewa-callback.ts): a standalone Express router, outside every ts-rest contract,
    // mounted with `express.raw()` before the app's JSON parser. See
    // apps/api/src/routes/billing-webhook.ts.

    submitOfflineProof: {
      method: 'POST',
      path: '/invoices/:invoiceId/offline-proof',
      pathParams: z.object({ invoiceId: uuidSchema }),
      headers: idempotencyHeadersSchema,
      body: submitOfflineProofBodySchema,
      responses: {
        200: invoiceSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Invoice is not `unpaid`/`pending_verification` — already paid, or not an offline invoice. */
        409: errorBodySchema,
      },
      summary: 'Submit a UTR + deposit-slip proof for an offline-payment invoice, for platform review',
      metadata: routeMeta('org.billing:update'),
    },

    listInvoices: {
      method: 'GET',
      path: '/invoices',
      query: listInvoicesQuerySchema,
      responses: {
        200: paginatedSchema(invoiceSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: "List the org's invoices",
      metadata: routeMeta('org.billing:read'),
    },

    getInvoice: {
      method: 'GET',
      path: '/invoices/:invoiceId',
      pathParams: z.object({ invoiceId: uuidSchema }),
      responses: {
        200: invoiceSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Get one invoice, with its offline-payment proof history if any',
      metadata: routeMeta('org.billing:read'),
    },

    getInvoicePdf: {
      method: 'GET',
      path: '/invoices/:invoiceId/pdf',
      pathParams: z.object({ invoiceId: uuidSchema }),
      responses: {
        200: invoiceDownloadSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Mint a short-lived link to the invoice PDF, rendering and storing it on first request',
      metadata: routeMeta('org.billing:read'),
    },
  },
  { pathPrefix: '/billing' },
);
